import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ClubPanel, PanelHead, PillButton, Face, CYAN, MUTED, SUBTLE } from './clubUi';
import { monthYearLabel, countdown } from '../../utils/surfaceFormat';
import LoadFailed from './LoadFailed';
import { openedLine } from '../../utils/portalWords';

/**
 * The club rail (README §1 "Rail", 380px, sticky): the variant panel from `clubViewerState.rail`
 * (price 32/800 + unit, perks with cyan tick circles, body, CTA h52, secondary #F2F2F2 h44, fine
 * print) → admin NEEDS YOU (black, cyan count pill, #1F1F1F pill rows) → THIS CLUB IN NUMBERS
 * (2×2 grey cells) → LIVE PORTALS (members, cyan + SEND) → SHARE / MUTE pills.
 * On phones the same panels stack under the HOME digest so nothing is lost.
 */
function Tick() {
  return (
    <span className="flex flex-none items-center justify-center rounded-full" style={{ width: 18, height: 18, background: CYAN }} aria-hidden="true">
      <svg width="10" height="10" viewBox="0 0 12 12" fill="none" stroke="#000" strokeWidth="2" strokeLinecap="square"><path d="M2 6 L5 9 L10 3" /></svg>
    </span>
  );
}

export function VariantPanel({ rail, onAction, phone = false, testId = 'rail-variant' }) {
  if (!rail) return null;
  return (
    <ClubPanel phone={phone} data-testid={testId}>
      <PanelHead title={rail.band} right={rail.right} />
      {rail.price && (
        <span className="flex items-baseline" style={{ gap: 8 }}>
          <span style={{ fontSize: 32, fontWeight: 800, lineHeight: 1 }} data-testid="rail-price">{rail.price}</span>
          <span style={{ fontSize: 12, color: MUTED }}>{rail.priceNote}</span>
        </span>
      )}
      {rail.perks?.length > 0 && (
        <span className="flex flex-col" style={{ gap: 8 }} data-testid="rail-perks">
          {rail.perks.map((p) => <span key={p} className="flex items-start" style={{ gap: 10, fontSize: 12, lineHeight: 1.5 }}><Tick />{p}</span>)}
        </span>
      )}
      {rail.body && <p className="m-0" style={{ fontSize: 13, lineHeight: 1.6 }} data-testid="rail-body">{rail.body}</p>}
      {rail.cta && rail.action && <PillButton variant={rail.fill ? 'primary' : 'secondary'} size={52} fontSize={12} onClick={() => onAction(rail.action)} data-testid="rail-cta">{rail.cta}</PillButton>}
      {rail.cta2 && rail.action2 && <PillButton variant="tertiary" size={44} fontSize={11} weight={700} onClick={() => onAction(rail.action2)} data-testid="rail-cta2">{rail.cta2}</PillButton>}
      {rail.fine && <span style={{ fontSize: 11, lineHeight: 1.55, color: MUTED }}>{rail.fine}</span>}
    </ClubPanel>
  );
}

export function NeedsYou({ items, onAction, phone = false }) {
  const total = items.reduce((n, i) => n + i.count, 0);
  if (items.length === 0) return null;
  return (
    <ClubPanel phone={phone} bg="#000" fg="#fff" data-testid="rail-needs-you">
      <PanelHead title="NEEDS YOU"><span className="rounded-full" style={{ padding: '5px 10px', background: CYAN, color: '#000', fontSize: 9, fontWeight: 800, letterSpacing: '.14em' }}>{total}</span></PanelHead>
      <div className="flex flex-col" style={{ gap: 6 }}>
        {items.map((i) => (
          <button key={i.label} type="button" onClick={() => onAction(i.action)} className="flex items-center justify-between font-mono uppercase transition-colors hover:bg-white hover:text-black" style={{ gap: 10, height: 46, padding: '0 18px', border: 'none', borderRadius: 999, background: '#1F1F1F', color: '#fff', fontSize: 11, fontWeight: 700, letterSpacing: '.1em', cursor: 'pointer' }} data-testid={`needs-${i.action}`}>
            <span>{i.label}</span><span>→</span>
          </button>
        ))}
      </div>
    </ClubPanel>
  );
}

export function ClubNumbers({ club, postsPerWeek, phone = false }) {
  const cells = [
    [club.memberCount ?? 0, 'MEMBERS'],
    [club.eventCount ?? 0, 'EVENTS'],
    [postsPerWeek ?? 0, 'POSTS / WEEK'],
    [monthYearLabel(club.createdAt).replace(/ (\d\d)(\d\d)$/, " '$2"), 'SINCE'],
  ];
  return (
    <ClubPanel phone={phone} data-testid="rail-numbers">
      <PanelHead title="THIS CLUB IN NUMBERS" />
      <div className="grid overflow-hidden" style={{ gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 4, borderRadius: 18 }}>
        {cells.map(([n, k]) => (
          <span key={k} className="flex flex-col items-center text-center" style={{ gap: 4, padding: '16px 8px', background: '#F5F5F5' }}>
            <span style={{ fontSize: 20, fontWeight: 800 }}>{n}</span>
            <span className="uppercase" style={{ fontSize: 9, fontWeight: 700, letterSpacing: '.14em', color: MUTED }}>{k}</span>
          </span>
        ))}
      </div>
    </ClubPanel>
  );
}

export function LiveSignals({ club, signals, loading, canSend, error = false, onRetry, phone = false }) {
  const navigate = useNavigate();
  return (
    <ClubPanel phone={phone} data-testid="rail-signals">
      <PanelHead title="LIVE PORTALS">
        {canSend && <PillButton variant="cyan" size={30} fontSize={9} className="hover:bg-black hover:text-[#00FFFF]" onClick={() => navigate(`/portals/new?club=${club.id}`)} data-testid="signal-send">+ SEND</PillButton>}
      </PanelHead>
      <div className="flex flex-col" style={{ gap: 8 }}>
        {loading && <span className="uppercase" style={{ fontSize: 9, fontWeight: 700, letterSpacing: '.1em', color: SUBTLE }}>LOADING…</span>}
        {/* A failed request is not an empty club, and must not read as one. */}
        {!loading && error && <LoadFailed what="the Portals in this club" onRetry={onRetry} testId="signals-failed" />}
        {!loading && !error && signals.length === 0 && <span style={{ fontSize: 12, color: MUTED }}>No live Portals in this club. Send the first one.</span>}
        {signals.map((s) => {
          const c = s.expiresAtUtc ? countdown(s.expiresAtUtc) : null;
          return (
            <button key={s.eventId} type="button" onClick={() => navigate(`/portals/${s.eventId}`)} className="flex items-center text-left font-mono" style={{ gap: 12, padding: '10px 14px 10px 10px', borderRadius: 18, background: '#F5F5F5', border: 'none', cursor: 'pointer', color: 'inherit' }} data-testid={`signal-${s.eventId}`}>
              <Face src={s.hostAvatarUrl} name={s.hostDisplayName} size={40} tintKey={s.hostUserId || s.eventId} style={{ boxShadow: `0 0 0 2px ${CYAN}` }} />
              <span className="flex min-w-0 flex-col" style={{ gap: 3 }}>
                <span style={{ fontSize: 12, fontWeight: 700, lineHeight: 1.4, overflowWrap: 'anywhere' }}>{s.signalText ? `${s.hostDisplayName ? `${s.hostDisplayName.split(' ')[0]}: ` : ''}${s.signalText}` : openedLine(s.hostDisplayName)}</span>
                <span className="uppercase" style={{ fontSize: 9, fontWeight: 700, letterSpacing: '.14em', color: MUTED }}>{c ? (c.expired ? 'EXPIRED' : `EXPIRES IN ${c.text}`) : 'LIVE'}</span>
              </span>
            </button>
          );
        })}
      </div>
    </ClubPanel>
  );
}

export default function ClubRail({
  club, state, needs = [], postsPerWeek, signals = [], signalsLoading = false, signalsError = false, onRetryLoad,
  showSignals = false, muted = false, onAction, mobile = false,
}) {
  return (
    <div className="flex flex-col font-mono" style={{ gap: mobile ? 10 : 12 }} data-testid="club-rail">
      <VariantPanel rail={state.rail} onAction={onAction} phone={mobile} />
      {state.isAdmin && <NeedsYou items={needs} onAction={onAction} phone={mobile} />}
      <ClubNumbers club={club} postsPerWeek={postsPerWeek} phone={mobile} />
      {showSignals && <LiveSignals club={club} signals={signals} loading={signalsLoading} error={signalsError} onRetry={onRetryLoad} canSend={state.isMember && club.memberSignalsEnabled !== false} phone={mobile} />}
      <div className="flex" style={{ gap: 6 }}>
        <PillButton variant="ghost" size={44} fontSize={10} style={{ flex: 1 }} onClick={() => onAction('share')} data-testid="rail-share">SHARE</PillButton>
        {state.isMember && <PillButton variant={muted ? 'primary' : 'ghost'} size={44} fontSize={10} style={{ flex: 1 }} onClick={() => onAction('mute')} aria-pressed={muted} data-testid="rail-mute">{muted ? 'UNMUTE' : 'MUTE'}</PillButton>}
      </div>
    </div>
  );
}
